import * as React from 'react'
import { styled } from 'next-yak'

import { useDramaticallyInefficientGlobalCss, css } from '@owl-nest/yak/extra-runtime'

import { transition, useTransition } from '../behaviours/transition'
import * as glyphs from '../icons/glyphs/index'
import * as S from '../styles'
import { type ButtonProps, Button } from './button/Button'
import { type LinkAsButtonProps, LinkAsButton } from './button/LinkAsButton'
import { ButtonAsLink, type ButtonAsLinkProps } from './button/ButtonAsLink'
import { type LinkProps, Link } from './Link'

type Button =
  | { type: 'button'; props: ButtonProps & { 'data-testid'?: string } }
  | { type: 'link'; props: LinkProps & { 'data-testid'?: string } }
  | { type: 'linkAsButton'; props: LinkAsButtonProps & { 'data-testid'?: string } }
  | { type: 'buttonAsLink'; props: ButtonAsLinkProps & { 'data-testid'?: string } }

export type ModalButtons = Button | [Button] | [Button, Button]

type Transition = 'from-bottom' | 'from-right'

export type ModalProps = {
  'data-testid'?: string
  buttons?: ModalButtons
  children: React.ReactNode
  className?: string
  closable?: boolean
  forceInitMount?: boolean
  lockScroll?: boolean
  onClose?: () => void
  open: boolean
  overlayScrollEnabled?: boolean
  transition?: Transition
}

export function ModalComponent({
  buttons,
  children,
  className,
  closable = false,
  forceInitMount = false,
  lockScroll = false,
  onClose,
  open,
  overlayScrollEnabled = false,
  transition = 'from-bottom',
  ...rest
}: ModalProps): React.ReactElement<ModalProps> | null {
  const modalRef = React.useRef<HTMLDivElement>(null)

  function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (modalRef.current === null) {
      return
    }
    if (closable && onClose && !modalRef.current.contains(event.target as Node)) {
      onClose()
    }
  }

  const { step, unmounted, onTransitionEnd } = useTransition(open)

  // NOTE: forceInitMount is used to skip the mount animation, but !open keeps the unmount animation
  if (unmounted && (forceInitMount === false || !open)) {
    return null
  }

  return (
    <S.modal.Overlay
      $step={!forceInitMount || !open ? step : 'during'}
      className={className}
      onClick={handleOverlayClick}
      $scrollEnabled={overlayScrollEnabled}
    >
      {lockScroll && <DisableBodyScroll />}
      <Wrapper
        testid={rest['data-testid']}
        transition={transition}
        step={step}
        onTransitionEnd={onTransitionEnd}
        ref={modalRef}
      >
        {closable && <glyphs.stroke.Close size={14} onClick={() => onClose?.()} />}
        <S.modal.Body>{children}</S.modal.Body>
        {buttons !== undefined && (
          <S.modal.Actions>
            {Array.isArray(buttons) ? (
              buttons.map((button, index) => {
                return <Action button={button} key={index} />
              })
            ) : (
              <Action button={buttons} />
            )}
          </S.modal.Actions>
        )}
      </Wrapper>
    </S.modal.Overlay>
  )
}

export const Modal = styled(ModalComponent)<ModalProps>``
Modal.displayName = 'ModalWithButtons'

type ActionProps = { button: Button }

function Action({ button }: ActionProps): React.ReactElement<any> {
  switch (button.type) {
    case 'button':
      return <Button type={button.type} {...button.props} />

    case 'buttonAsLink':
      return <ButtonAsLink {...button.props} />

    case 'linkAsButton':
      return <LinkAsButton {...button.props} withPadding={false} />

    case 'link':
      return <Link {...button.props} />

    default:
      return <></>
  }
}

function DisableBodyScroll() {
  useDramaticallyInefficientGlobalCss(css`
    body,
    html {
      overflow: hidden;
    }
  `)

  return <></>
}

type WrapperProps = {
  testid?: string
  transition: Transition
  step: 'before' | 'during' | 'after'
  onTransitionEnd: React.TransitionEventHandler<HTMLDivElement>
  children: React.ReactNode
}

const Wrapper = React.forwardRef<HTMLDivElement, WrapperProps>(function Wrapper(
  { transition, children, onTransitionEnd, step, testid },
  ref,
) {
  switch (transition) {
    case 'from-bottom':
      return (
        <TransitionFromBottom data-testid={testid} onTransitionEnd={onTransitionEnd} step={step} ref={ref}>
          {children}
        </TransitionFromBottom>
      )
    case 'from-right':
      return (
        <TransitionFromRight data-testid={testid} onTransitionEnd={onTransitionEnd} step={step} ref={ref}>
          {children}
        </TransitionFromRight>
      )
  }
})

const TransitionFromBottom = transition.fromBottom(S.modal.Wrapper)

const TransitionFromRight = transition.fromRight(S.modal.Wrapper)
